<script lang="ts">
    import Fa from 'svelte-fa'
    import {
        faArrowsAlt,
        faCheck,
        faEraser,
        faExpand,
        faMagnifyingGlassMinus,
        faMagnifyingGlassPlus,
        faPen,
        faUpDownLeftRight,
        faUpload,
        faXmark,
    } from '@fortawesome/free-solid-svg-icons'
    import { faCircle } from '@fortawesome/free-regular-svg-icons'
    import FieldHeading from './field-heading.svelte'
    import Zdraggable from '../../../_common/z/zdraggable.svelte'
    import { getMarkerTools, type AnnotationPoint } from '../../types/marker-types'
    import { PHOTO_MAX_HEIGHT, PHOTO_MAX_WIDTH, PHOTO_QUALITY } from '../../utils/image-limits'

    interface ImageAnnotationItem {
        id: string
        name: string
        dataUrl: string
        points?: AnnotationPoint[]
    }

    export let label: string
    export let data: Record<string, any>
    export let value: ImageAnnotationItem[] = []
    export let required = false
    export let readOnly = false

    const colorOptions = ['#e03131', '#212529', '#1971c2', '#2f9e44', '#f08c00']

    type Tool = 'pencil' | 'eraser' | 'circle' | string

    // Ferramentas de marcação são configuradas por modelo (block.data.markerTypes) — cai no padrão
    // fixo só para modelos criados antes dessa personalização existir.
    $: tools = getMarkerTools(data)
    $: toolsById = Object.fromEntries(tools.map((t) => [t.id, t]))

    let fileInputEl: HTMLInputElement
    let imgEl: HTMLImageElement
    let canvasEl: HTMLCanvasElement
    let stageWidth = 0
    let stageHeight = 0

    let activeIndex: number | null = null
    let imgLoaded = false
    let drawing = false
    let tool: Tool = 'pencil'
    let color = colorOptions[0]
    let brushSize = 5
    let editingPointId: string | null = null
    let circleSnapshot = ''
    let circleStart = { x: 0, y: 0 }
    // Marcador em seta (ex: Fio de PDO) precisa de 2 cliques: o primeiro cria o ponto com a cauda
    // na posição clicada, este id fica "pendente" até o segundo clique definir a ponta (x2/y2).
    let pendingArrowId: string | null = null
    // Posição do mouse sobre o canvas (em px de tela), usada para desenhar um círculo do tamanho
    // real da borracha em vez do cursor padrão — o "X" do crosshair não deixa claro o diâmetro.
    let eraserCursorPos: { x: number; y: number } | null = null

    // Zoom/deslocamento do palco, no mesmo modelo do bloco "Antes e depois": a ampliação é só
    // visual (transform CSS), nada disso entra no que é salvo. Como toda a matemática de desenho e
    // de posicionamento dos marcadores parte de getBoundingClientRect() — que já vem com o
    // transform aplicado —, as coordenadas continuam corretas em qualquer nível de zoom.
    /**
     * Teto do lado maior do canvas de marcação (ver onImageLoad). Acima de ~16,7 MP de área o Safari
     * no iOS não aloca o canvas; 2400 mantém qualquer proporção bem abaixo disso. Também está acima
     * do limite de upload das fotos (2000, em utils/image-limits.ts), então foto nova não perde nada.
     */
    const MAX_CANVAS_SIDE = 2400

    const MIN_ZOOM = 1
    const MAX_ZOOM = 4
    let zoom = 1
    let offsetX = 0
    let offsetY = 0
    let panning = false
    let panOrigin = { x: 0, y: 0 }
    let viewportEl: HTMLDivElement

    function clamp(n: number, min: number, max: number) {
        return Math.min(max, Math.max(min, n))
    }

    // Limite de arrasto: com a imagem ampliada, metade do excedente pode ser deslocada para cada
    // lado antes de aparecer área vazia.
    function maxOffsetFor(z: number) {
        return Math.max(0, (z - 1) * 50)
    }

    function zoomBy(delta: number) {
        const next = clamp(zoom + delta, MIN_ZOOM, MAX_ZOOM)
        if (next === zoom) return
        zoom = next
        const maxOffset = maxOffsetFor(zoom)
        offsetX = clamp(offsetX, -maxOffset, maxOffset)
        offsetY = clamp(offsetY, -maxOffset, maxOffset)
        if (zoom === MIN_ZOOM) {
            offsetX = 0
            offsetY = 0
        }
    }

    function resetZoom() {
        zoom = 1
        offsetX = 0
        offsetY = 0
        panning = false
    }

    function onViewportWheel(event: WheelEvent) {
        if (!imgLoaded) return
        event.preventDefault()
        zoomBy(event.deltaY < 0 ? 0.15 : -0.15)
    }

    function panBy(dxPx: number, dyPx: number) {
        if (!viewportEl) return
        const rect = viewportEl.getBoundingClientRect()
        const maxOffset = maxOffsetFor(zoom)
        // O translate é em % do próprio palco; converter o arrasto (px de tela) pela largura já
        // ampliada mantém o movimento na mesma velocidade do ponteiro em qualquer zoom.
        offsetX = clamp(offsetX + (dxPx / (rect.width * zoom)) * 100, -maxOffset, maxOffset)
        offsetY = clamp(offsetY + (dyPx / (rect.height * zoom)) * 100, -maxOffset, maxOffset)
    }

    function isMarkerTool(t: Tool): boolean {
        return t in toolsById
    }

    function isArrowType(type: string) {
        return toolsById[type]?.shape === 'arrow'
    }

    function autofocus(node: HTMLInputElement) {
        node.focus()
    }

    function cancelPendingArrow() {
        if (!pendingArrowId || activeIndex === null) return
        const item = value[activeIndex]
        item.points = (item.points ?? []).filter((p) => p.id !== pendingArrowId)
        value = value
        pendingArrowId = null
    }

    function selectTool(t: Tool) {
        cancelPendingArrow()
        tool = t
        editingPointId = null
        if (t !== 'eraser') eraserCursorPos = null
    }

    function arrowGeometry(point: AnnotationPoint) {
        const x2 = point.x2 ?? point.x
        const y2 = point.y2 ?? point.y
        const dxPx = ((x2 - point.x) / 100) * stageWidth
        const dyPx = ((y2 - point.y) / 100) * stageHeight
        const length = Math.hypot(dxPx, dyPx)
        const angle = (Math.atan2(dyPx, dxPx) * 180) / Math.PI
        return { length, angle }
    }

    function onFilesSelected(event: Event) {
        if (readOnly) return
        const input = event.currentTarget as HTMLInputElement
        const files = input.files ? Array.from(input.files) : []
        for (const file of files) {
            const reader = new FileReader()
            reader.onload = async () => {
                // Foto de celular chega com 3000-4000px; a impressão usa ~440px. Ver utils/image-limits.ts.
                const dataUrl = await window.api.utils.images.resizeBase64(reader.result as string, PHOTO_MAX_WIDTH, PHOTO_MAX_HEIGHT, PHOTO_QUALITY)
                value = [...value, { id: crypto.randomUUID(), name: file.name, dataUrl, points: [] }]
            }
            reader.readAsDataURL(file)
        }
        input.value = ''
    }

    function removeImage(index: number) {
        if (readOnly) return
        value = value.filter((_, i) => i !== index)
    }

    function openEditor(index: number) {
        imgLoaded = false
        tool = 'pencil'
        editingPointId = null
        pendingArrowId = null
        resetZoom()
        activeIndex = index
    }

    function onImageLoad() {
        if (!imgEl || !canvasEl || !imgEl.naturalWidth) return

        // O Safari no iOS não aloca canvas acima de ~16,7 milhões de pixels de área. Foto de iPhone
        // recente tem 48 MP — quase três vezes isso. O canvas então não é criado, e com ele vai
        // embora o desenho E a detecção de toque: como o `pointerdown` do lápis e o `click` dos
        // marcadores estão todos neste mesmo elemento, nenhuma ferramenta responde. No desktop o
        // teto é muito maior, por isso só aparecia no iPhone.
        //
        // O canvas não precisa da resolução da foto: ele guarda só o traço, exibido em algumas
        // centenas de pixels. O `closeEditor` monta o canvas de mesclagem a partir destas mesmas
        // dimensões, então a foto achatada acompanha sem desalinhar.
        const scale = Math.min(1, MAX_CANVAS_SIDE / Math.max(imgEl.naturalWidth, imgEl.naturalHeight))
        canvasEl.width = Math.round(imgEl.naturalWidth * scale)
        canvasEl.height = Math.round(imgEl.naturalHeight * scale)

        const ctx = canvasEl.getContext('2d')
        if (!ctx) {
            // Antes isto era `ctx?.clearRect(...)` seguido de `imgLoaded = true`: a falha passava
            // batida e as ferramentas ficavam inertes sem nenhuma pista do motivo.
            window.error('Não foi possível preparar a área de marcação desta foto.')
            return
        }
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height)
        imgLoaded = true
    }

    // A miniatura já renderiza a mesma `src` antes de o editor abrir, então o <img> daqui quase nunca
    // nasce com imagem por carregar — ele nasce com ela pronta. Nesse caso o evento `load` pode nunca
    // chegar ao handler (o Chrome dispara assim mesmo; o Safari nem sempre), e `imgLoaded` ficaria
    // `false` para sempre. Como esse é o portão de pointerDown, onStageClick e pointerMove, o efeito
    // é o editor abrir com a foto visível e nenhuma ferramenta responder ao toque.
    // `complete` cobre a imagem que já chegou; o `on:load` segue valendo para a que ainda vai chegar.
    $: if (activeIndex !== null && imgEl && canvasEl && !imgLoaded && imgEl.complete) onImageLoad()

    function getCanvasScale() {
        const rect = canvasEl.getBoundingClientRect()
        return canvasEl.width / rect.width
    }

    function getPos(event: PointerEvent, scale: number) {
        const rect = canvasEl.getBoundingClientRect()
        return {
            x: (event.clientX - rect.left) * scale,
            y: (event.clientY - rect.top) * scale,
        }
    }

    function getPercentPos(clientX: number, clientY: number) {
        const rect = canvasEl.getBoundingClientRect()
        return {
            x: Math.round(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)) * 100) / 100,
            y: Math.round(Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)) * 100) / 100,
        }
    }

    function pointerDown(event: PointerEvent) {
        if (!imgLoaded) return
        // Em somente leitura o arrasto sempre movimenta: não há desenho pra disputar o gesto, e a
        // paleta (de onde se escolhe "Mover") nem é exibida nesse modo.
        if (tool === 'pan' || readOnly) {
            panning = true
            panOrigin = { x: event.clientX, y: event.clientY }
            return
        }
        if (isMarkerTool(tool)) return
        drawing = true
        const ctx = canvasEl.getContext('2d')
        if (!ctx) return
        const scale = getCanvasScale()
        const pos = getPos(event, scale)
        if (tool === 'circle') {
            circleSnapshot = canvasEl.toDataURL('image/png')
            circleStart = pos
        }
        ctx.beginPath()
        ctx.moveTo(pos.x, pos.y)
    }

    function pointerMove(event: PointerEvent) {
        if (panning) {
            panBy(event.clientX - panOrigin.x, event.clientY - panOrigin.y)
            panOrigin = { x: event.clientX, y: event.clientY }
            return
        }

        if (tool === 'eraser' && imgLoaded && canvasEl) {
            const rect = canvasEl.getBoundingClientRect()
            eraserCursorPos = { x: event.clientX - rect.left, y: event.clientY - rect.top }
        } else if (eraserCursorPos) {
            eraserCursorPos = null
        }

        if (pendingArrowId && activeIndex !== null && imgLoaded) {
            const item = value[activeIndex]
            const point = (item.points ?? []).find((p) => p.id === pendingArrowId)
            if (point) {
                const pos = getPercentPos(event.clientX, event.clientY)
                point.x2 = pos.x
                point.y2 = pos.y
                value = value
            }
        }

        if (!drawing || !imgLoaded) return
        const ctx = canvasEl.getContext('2d')
        if (!ctx) return
        const scale = getCanvasScale()
        const pos = getPos(event, scale)

        if (tool === 'circle') {
            const snapshot = new Image()
            snapshot.onload = () => {
                ctx.clearRect(0, 0, canvasEl.width, canvasEl.height)
                ctx.drawImage(snapshot, 0, 0)
                const radius = Math.hypot(pos.x - circleStart.x, pos.y - circleStart.y)
                ctx.globalCompositeOperation = 'source-over'
                ctx.beginPath()
                ctx.arc(circleStart.x, circleStart.y, radius, 0, Math.PI * 2)
                ctx.strokeStyle = color
                ctx.lineWidth = brushSize * scale
                ctx.stroke()
            }
            snapshot.src = circleSnapshot
            return
        }

        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        // A área realmente apagada precisa bater com o círculo mostrado em .eraser-cursor (brushSize * 4) —
        // mesmo multiplicador aqui, só que convertido pra escala interna do canvas.
        ctx.lineWidth = (tool === 'eraser' ? brushSize * 4 : brushSize) * scale
        ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over'
        ctx.strokeStyle = color
        ctx.lineTo(pos.x, pos.y)
        ctx.stroke()
    }

    function pointerUp() {
        drawing = false
        panning = false
    }

    function pointerLeave() {
        drawing = false
        panning = false
        eraserCursorPos = null
    }

    function onStageClick(event: MouseEvent) {
        if (readOnly || !imgLoaded || activeIndex === null) return
        if (!isMarkerTool(tool)) return
        const item = value[activeIndex]
        const pos = getPercentPos(event.clientX, event.clientY)

        if (isArrowType(tool)) {
            if (pendingArrowId) {
                // 2º clique: define a ponta da seta e abre o editor de valor
                const point = (item.points ?? []).find((p) => p.id === pendingArrowId)
                pendingArrowId = null
                if (!point) return
                point.x2 = pos.x
                point.y2 = pos.y
                value = value
                editingPointId = point.id
                return
            }
            // 1º clique: cria a seta com a cauda na posição clicada, aguardando a ponta
            const point: AnnotationPoint = { id: crypto.randomUUID(), type: tool, x: pos.x, y: pos.y, x2: pos.x, y2: pos.y, value: '' }
            item.points = [...(item.points ?? []), point]
            value = value
            pendingArrowId = point.id
            return
        }

        const point: AnnotationPoint = { id: crypto.randomUUID(), type: tool, x: pos.x, y: pos.y, value: '' }
        item.points = [...(item.points ?? []), point]
        value = value
        editingPointId = point.id
    }

    function openPointEdit(pointId: string) {
        if (readOnly) return
        editingPointId = pointId
    }

    function removePoint(pointId: string) {
        if (readOnly || activeIndex === null) return
        const item = value[activeIndex]
        item.points = (item.points ?? []).filter((p) => p.id !== pointId)
        value = value
        if (editingPointId === pointId) editingPointId = null
    }

    function closeEditor() {
        if (activeIndex === null) return
        editingPointId = null
        cancelPendingArrow()
        const item = value[activeIndex]
        if (!item || !imgLoaded || readOnly) {
            activeIndex = null
            imgLoaded = false
            return
        }

        // Fotos já enviadas ficam hospedadas no S3 (URL cross-origin). imgEl já carrega com
        // crossorigin="anonymous" (ver markup) desde o primeiro load — usar uma segunda cópia da
        // imagem sem esse atributo desde o início fazia o Chrome reaproveitar a resposta em cache da
        // exibição normal (sem CORS) para a busca com CORS, que falha por não ter o header
        // Access-Control-Allow-Origin registrado nessa entrada de cache. Uma única busca, sempre em
        // modo CORS, evita esse descompasso de cache.
        // Os pontos marcadores (points) ficam de fora da mesclagem de propósito: continuam como
        // dados estruturados para permanecerem interativos (hover com o valor) na visualização.
        try {
            const tmp = document.createElement('canvas')
            tmp.width = canvasEl.width
            tmp.height = canvasEl.height
            const ctx = tmp.getContext('2d')
            if (ctx) {
                // Fundo branco antes da foto: o resultado vai sair em JPEG, que não tem canal alpha —
                // sem isto, uma origem com transparência viraria preto nessas áreas.
                ctx.fillStyle = '#ffffff'
                ctx.fillRect(0, 0, tmp.width, tmp.height)
                ctx.drawImage(imgEl, 0, 0, tmp.width, tmp.height)
                ctx.drawImage(canvasEl, 0, 0)
                // JPEG, não PNG: o conteúdo é fotografia, e PNG (sem perda) gera um arquivo muitas
                // vezes maior para o mesmo resultado visual — peso que depois reaparece no documento,
                // na memória de quem renderiza e no PDF. A qualidade é a mesma das fotos de prontuário.
                item.dataUrl = tmp.toDataURL('image/jpeg', PHOTO_QUALITY)
                value = value
            }
        } catch (error) {
            window.error('Não foi possível salvar as marcações desta foto. O servidor de imagens não permite esse tipo de edição no momento.')
        }
        activeIndex = null
        imgLoaded = false
    }
</script>

<div class="annotation-field">
    <FieldHeading {label} {required} {readOnly} />

    {#if !readOnly}
        <div class="upload-row">
            <button type="button" class="upload-btn" on:click={() => fileInputEl.click()}>
                <Fa icon={faUpload} />
                <span>Selecionar fotos</span>
            </button>
            <input bind:this={fileInputEl} type="file" accept="image/*" multiple hidden on:change={onFilesSelected} />
        </div>
    {/if}

    {#if value.length > 0}
        <div class="thumbnail-grid">
            {#each value as image, index (image.id)}
                <div class="thumbnail">
                    <button type="button" class="thumbnail-image-btn" on:click={() => openEditor(index)} title={image.name}>
                        <img src={image.dataUrl} alt={image.name} crossorigin="anonymous" />
                    </button>
                    {#if !readOnly}
                        <button type="button" class="thumbnail-remove" title="Remover" on:click={() => removeImage(index)}>
                            <Fa icon={faXmark} />
                        </button>
                    {/if}
                </div>
            {/each}
        </div>
    {:else}
        <p class="empty-hint">Nenhuma foto selecionada ainda.</p>
    {/if}
</div>

{#if activeIndex !== null && value[activeIndex]}
    <div class="editor-backdrop">
        <div class="editor-modal">
            <div class="editor-toolbar">
                {#if readOnly}
                    <span class="readonly-notice">
                        <Fa icon={faXmark} />
                        Somente leitura
                    </span>
                {/if}
                <div class="zoom-control">
                    <button type="button" title="Reduzir zoom" disabled={zoom <= MIN_ZOOM} on:click={() => zoomBy(-0.25)}>
                        <Fa icon={faMagnifyingGlassMinus} />
                    </button>
                    <span class="zoom-value">{Math.round(zoom * 100)}%</span>
                    <button type="button" title="Ampliar zoom" disabled={zoom >= MAX_ZOOM} on:click={() => zoomBy(0.25)}>
                        <Fa icon={faMagnifyingGlassPlus} />
                    </button>
                    <button type="button" title="Enquadrar" disabled={zoom === MIN_ZOOM && offsetX === 0 && offsetY === 0} on:click={resetZoom}>
                        <Fa icon={faExpand} />
                    </button>
                </div>
                <div class="spacer" />
                <button type="button" class="done-btn" on:click={closeEditor}>
                    <Fa icon={faCheck} />
                    <span>{readOnly ? 'Fechar' : 'Concluir'}</span>
                </button>
            </div>

            <!-- O viewport recorta o excedente quando o palco é ampliado e é quem escuta a rodinha
                 do mouse; o transform fica no palco pra que canvas, foto e marcadores ampliem juntos. -->
            <div class="stage-viewport" bind:this={viewportEl} on:wheel={onViewportWheel}>
                <div
                    class="canvas-stage"
                    class:panning
                    style="transform: translate({offsetX}%, {offsetY}%) scale({zoom})"
                    bind:clientWidth={stageWidth}
                    bind:clientHeight={stageHeight}
                >
                    <img
                        bind:this={imgEl}
                        crossorigin="anonymous"
                        src={value[activeIndex].dataUrl}
                        alt={value[activeIndex].name}
                        class="photo-layer"
                        on:load={onImageLoad}
                    />
                    <canvas
                        bind:this={canvasEl}
                        class="annotation-layer"
                        class:readonly={readOnly}
                        class:marker-cursor={isMarkerTool(tool)}
                        class:pan-cursor={tool === 'pan'}
                        class:eraser-active={tool === 'eraser'}
                        on:pointerdown={pointerDown}
                        on:pointermove={pointerMove}
                        on:pointerup={pointerUp}
                        on:pointerleave={pointerLeave}
                        on:click={onStageClick}
                    />
                    {#if tool === 'eraser' && eraserCursorPos}
                        <div
                            class="eraser-cursor"
                            style="left:{eraserCursorPos.x}px; top:{eraserCursorPos.y}px; width:{brushSize * 4}px; height:{brushSize * 4}px;"
                        />
                    {/if}
                    <div class="points-layer">
                        {#each value[activeIndex].points ?? [] as point (point.id)}
                            {@const marker = toolsById[point.type]}
                            {#if isArrowType(point.type)}
                                {@const geo = arrowGeometry(point)}
                                {#if pendingArrowId === point.id}
                                    <div
                                        class="arrow-preview"
                                        style="top:{point.y}%; left:{point.x}%; width:{geo.length}px; transform: translateY(-50%) rotate({geo.angle}deg); background:{marker?.color};"
                                    />
                                {:else}
                                    <button
                                        type="button"
                                        class="arrow-hit"
                                        style="top:{point.y}%; left:{point.x}%; width:{geo.length}px; transform: translateY(-50%) rotate({geo.angle}deg);"
                                        on:click|stopPropagation={() => openPointEdit(point.id)}
                                        disabled={readOnly}
                                        title={marker?.label}
                                    >
                                        <span class="arrow-line" style="background:{marker?.color};" />
                                        <span class="arrow-head" style="border-left-color:{marker?.color};" />
                                    </button>
                                    <div class="point-marker" style="top:{point.y2 ?? point.y}%; left:{point.x2 ?? point.x}%;">
                                        {#if editingPointId === point.id && !readOnly}
                                            <div class="point-editor" on:click|stopPropagation>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    bind:value={point.value}
                                                    use:autofocus
                                                    on:keydown={(ev) => {
                                                        if (ev.key === 'Enter') editingPointId = null
                                                    }}
                                                />
                                                <span class="point-unit">{marker?.unit}</span>
                                                <button type="button" class="point-confirm" on:click={() => (editingPointId = null)}>
                                                    <Fa icon={faCheck} />
                                                </button>
                                                <button type="button" class="point-delete" on:click={() => removePoint(point.id)}>
                                                    <Fa icon={faXmark} />
                                                </button>
                                            </div>
                                        {:else}
                                            <div class="point-label">{point.value} {marker?.unit}</div>
                                        {/if}
                                    </div>
                                {/if}
                            {:else}
                                <div class="point-marker" style="top:{point.y}%; left:{point.x}%;">
                                    <button
                                        type="button"
                                        class="point-dot"
                                        style="background:{marker?.color}; border-color:{marker?.color};"
                                        on:click|stopPropagation={() => openPointEdit(point.id)}
                                        disabled={readOnly}
                                        title={marker?.label}
                                    />
                                    {#if editingPointId === point.id && !readOnly}
                                        <div class="point-editor" on:click|stopPropagation>
                                            <input
                                                type="number"
                                                step="0.1"
                                                bind:value={point.value}
                                                use:autofocus
                                                on:keydown={(ev) => {
                                                    if (ev.key === 'Enter') editingPointId = null
                                                }}
                                            />
                                            <span class="point-unit">{marker?.unit}</span>
                                            <button type="button" class="point-confirm" on:click={() => (editingPointId = null)}>
                                                <Fa icon={faCheck} />
                                            </button>
                                            <button type="button" class="point-delete" on:click={() => removePoint(point.id)}>
                                                <Fa icon={faXmark} />
                                            </button>
                                        </div>
                                    {:else}
                                        <div class="point-label">{point.value} {marker?.unit}</div>
                                    {/if}
                                </div>
                            {/if}
                        {/each}
                    </div>
                </div>
            </div>
        </div>

        {#if !readOnly}
            <Zdraggable left={20} top={20}>
                <div class="marker-palette">
                    <div class="marker-palette-header"><Fa icon={faArrowsAlt} /></div>
                    <div class="marker-palette-body">
                        {#each tools as marker (marker.id)}
                            <button type="button" class="marker-option" class:active={tool === marker.id} on:click={() => selectTool(marker.id)}>
                                <span class="marker-dot" style="background:{marker.color}" />
                                <span class="marker-option-label">{marker.label}</span>
                            </button>
                        {/each}
                        <button type="button" class="marker-option" class:active={tool === 'pencil'} on:click={() => selectTool('pencil')}>
                            <span class="marker-icon"><Fa icon={faPen} /></span>
                            <span class="marker-option-label">Lápis</span>
                        </button>
                        <button type="button" class="marker-option" class:active={tool === 'circle'} on:click={() => selectTool('circle')}>
                            <span class="marker-icon"><Fa icon={faCircle} /></span>
                            <span class="marker-option-label">Círculo</span>
                        </button>
                        <button type="button" class="marker-option" class:active={tool === 'eraser'} on:click={() => selectTool('eraser')}>
                            <span class="marker-icon"><Fa icon={faEraser} /></span>
                            <span class="marker-option-label">Borracha</span>
                        </button>
                        <button type="button" class="marker-option" class:active={tool === 'pan'} on:click={() => selectTool('pan')}>
                            <span class="marker-icon"><Fa icon={faUpDownLeftRight} /></span>
                            <span class="marker-option-label">Mover</span>
                        </button>

                        {#if tool === 'pencil' || tool === 'circle'}
                            <div class="color-group">
                                {#each colorOptions as option (option)}
                                    <button
                                        type="button"
                                        class="color-swatch"
                                        class:active={color === option}
                                        style="background:{option}"
                                        aria-label={`Cor ${option}`}
                                        on:click={() => (color = option)}
                                    />
                                {/each}
                            </div>
                        {/if}

                        {#if !isMarkerTool(tool) && tool !== 'pan'}
                            <div class="size-control">
                                <input type="range" min="2" max="24" bind:value={brushSize} aria-label="Espessura" on:pointerdown|stopPropagation />
                            </div>
                        {/if}
                    </div>
                </div>
            </Zdraggable>
        {/if}
    </div>
{/if}

<style>
    .upload-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #ced4da;
        background: #fff;
        color: #495057;
        border-radius: 8px;
        padding: 8px 14px;
        font-size: 0.85rem;
        cursor: pointer;
    }
    .upload-btn:hover {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    .empty-hint {
        margin: 10px 0 0;
        font-size: 0.85rem;
        color: #adb5bd;
    }
    .thumbnail-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 14px;
    }
    .thumbnail {
        position: relative;
        width: 96px;
        height: 96px;
    }
    .thumbnail-image-btn {
        width: 100%;
        height: 100%;
        padding: 0;
        border: 1px solid #e9ecef;
        border-radius: 10px;
        overflow: hidden;
        cursor: pointer;
        background: #f1f3f5;
    }
    .thumbnail-image-btn img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
    .thumbnail-remove {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: none;
        background: #495057;
        color: #fff;
        font-size: 0.7rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }
    .thumbnail-remove:hover {
        background: #fa5252;
    }

    .editor-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1100;
        background: rgba(17, 19, 24, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }
    .editor-modal {
        position: relative;
        background: #fff;
        border-radius: 14px;
        padding: 16px;
        max-width: 95vw;
        max-height: 92vh;
        display: flex;
        flex-direction: column;
        gap: 14px;
        overflow: auto;
    }
    .editor-toolbar {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }
    .color-group {
        display: flex;
        gap: 6px;
        padding: 6px 4px 2px;
    }
    .color-swatch {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid transparent;
        cursor: pointer;
        padding: 0;
    }
    .color-swatch.active {
        border-color: #212529;
    }
    .size-control {
        display: flex;
        align-items: center;
        padding: 4px 8px;
    }
    .size-control input[type='range'] {
        flex: 1;
        width: 100%;
        margin: 0;
    }
    .done-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        border: 1px solid #dee2e6;
        background: #fff;
        color: #495057;
        border-radius: 8px;
        padding: 7px 12px;
        font-size: 0.82rem;
        cursor: pointer;
        margin-left: auto;
        background: var(--cs2-accent, #4263eb);
        border-color: var(--cs2-accent, #4263eb);
        color: #fff;
    }
    .done-btn:hover {
        background: #364fc7;
    }
    .stage-viewport {
        position: relative;
        display: inline-block;
        max-width: 100%;
        align-self: center;
        overflow: hidden;
        border-radius: 6px;
        /* Sem isso o navegador rola/dá zoom na página em vez de deixar a rodinha e o arrasto
           chegarem nos handlers do palco. */
        touch-action: none;
        overscroll-behavior: contain;
    }
    .canvas-stage {
        position: relative;
        display: inline-block;
        max-width: 100%;
        transform-origin: center center;
    }
    .canvas-stage.panning {
        cursor: grabbing;
    }
    .zoom-control {
        display: flex;
        align-items: center;
        gap: 2px;
    }
    .zoom-control button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        background: #fff;
        color: #495057;
        cursor: pointer;
        padding: 0;
    }
    .zoom-control button:hover:not(:disabled) {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    .zoom-control button:disabled {
        opacity: 0.45;
        cursor: default;
    }
    .zoom-value {
        min-width: 44px;
        text-align: center;
        font-size: 0.78rem;
        color: #868e96;
        font-variant-numeric: tabular-nums;
    }
    .photo-layer {
        display: block;
        max-width: 100%;
        max-height: 68vh;
        width: auto;
        height: auto;
        user-select: none;
        pointer-events: none;
        border-radius: 6px;
    }
    .annotation-layer {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        touch-action: none;
        cursor: crosshair;
    }
    .annotation-layer.marker-cursor {
        cursor: copy;
    }
    .annotation-layer.eraser-active {
        /* Cursor padrão escondido — o círculo em .eraser-cursor mostra o diâmetro real da borracha. */
        cursor: none;
    }
    .annotation-layer.pan-cursor,
    .annotation-layer.readonly {
        cursor: grab;
    }
    .eraser-cursor {
        position: absolute;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        border: 1.5px solid rgba(33, 37, 41, 0.85);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.35);
        pointer-events: none;
    }
    .readonly-notice {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.82rem;
        color: #868e96;
    }

    /* ───── paleta de marcadores flutuante ───── */
    /* Fica fora do .editor-modal (irmã dele, dentro do backdrop fixo) para poder ser arrastada
       livremente pela tela, inclusive por cima do fundo escuro fora do modal. Zdraggable usa
       position:absolute sem z-index — precisa de um valor explícito para renderizar acima do modal. */
    .editor-backdrop :global(.draggable) {
        z-index: 5;
    }
    .marker-palette {
        background: #f1f3f5;
        border-radius: 6px;
        width: 170px;
        font-size: 0.82rem;
        box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.25);
        overflow: hidden;
    }
    .marker-palette-header {
        background: #328866;
        padding: 3px 6px;
        color: #fff;
        cursor: move;
    }
    .marker-palette-body {
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .marker-option {
        display: flex;
        align-items: center;
        gap: 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        padding: 5px 6px;
        border-radius: 5px;
        color: #212529;
        text-align: left;
    }
    .marker-option:hover {
        background: rgba(16, 16, 16, 0.06);
    }
    .marker-option.active {
        background: rgba(16, 16, 16, 0.12);
    }
    .marker-dot {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        border-radius: 50%;
    }
    .marker-icon {
        flex-shrink: 0;
        width: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #495057;
    }
    .marker-option-label {
        white-space: nowrap;
    }

    /* ───── pontos marcadores sobre a foto ───── */
    .points-layer {
        position: absolute;
        inset: 0;
        pointer-events: none;
    }
    .point-marker {
        position: absolute;
        transform: translate(-50%, -50%);
        pointer-events: auto;
    }
    .point-dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
        padding: 0;
        cursor: pointer;
    }
    .point-dot:disabled {
        cursor: default;
    }
    .arrow-hit {
        position: absolute;
        height: 16px;
        padding: 0;
        border: none;
        background: none;
        cursor: pointer;
        transform-origin: 0 50%;
        pointer-events: auto;
    }
    .arrow-hit:disabled {
        cursor: default;
    }
    .arrow-line {
        position: absolute;
        top: 50%;
        left: 0;
        right: 8px;
        height: 3px;
        border-radius: 2px;
        transform: translateY(-50%);
    }
    .arrow-head {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 6px 0 6px 9px;
        border-color: transparent transparent transparent currentColor;
    }
    .arrow-preview {
        position: absolute;
        height: 3px;
        border-radius: 2px;
        opacity: 0.6;
        transform-origin: 0 50%;
        pointer-events: none;
    }
    .point-label {
        position: absolute;
        top: 50%;
        left: calc(100% + 6px);
        transform: translateY(-50%);
        white-space: nowrap;
        font-size: 0.76rem;
        color: #fff;
        background: rgba(17, 19, 24, 0.85);
        padding: 2px 7px;
        border-radius: 5px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.1s ease;
    }
    .point-marker:hover .point-label {
        opacity: 1;
    }
    /* Na seta, o ponto com o rótulo fica na ponta (irmão logo após o .arrow-hit) — sem alvo visível
       próprio para hover, então revela o rótulo ao passar o mouse na linha da seta. */
    .arrow-hit:hover + .point-marker .point-label {
        opacity: 1;
    }
    .point-editor {
        position: absolute;
        top: 50%;
        left: calc(100% + 6px);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        gap: 4px;
        background: rgba(50, 54, 61, 0.92);
        padding: 5px;
        border-radius: 6px;
        white-space: nowrap;
    }
    .point-editor input[type='number'] {
        width: 56px;
        border: none;
        border-radius: 4px;
        padding: 4px 6px;
        font-size: 0.82rem;
    }
    .point-unit {
        color: #fff;
        font-size: 0.78rem;
        padding: 0 2px;
    }
    .point-confirm,
    .point-delete {
        border: none;
        border-radius: 4px;
        width: 26px;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
    }
    .point-confirm {
        background: #495057;
        color: #fff;
    }
    .point-confirm:hover {
        background: #343a40;
    }
    .point-delete {
        background: #f8c400;
        color: #212529;
    }
    .point-delete:hover {
        background: #e0af00;
    }

    @media (max-width: 900px) {
        .editor-toolbar {
            gap: 10px;
        }
        .done-btn {
            margin-left: 0;
        }
    }
</style>
